// src/utils/qbMappingCache.js
// qbmapcache-4d7e9a2c
// Per-session, per-organization cache for getQuickBooksMappingOptions.
//
// getQuickBooksMappingOptions makes three Intuit round trips per call (an
// Account query plus TaxCode and TaxRate queries). Several surfaces fetch the
// same rarely-changing per-org payload on mount, so we cache the successful
// result in module memory for the life of the page and de-duplicate concurrent
// calls. Callers must gate on quickbooksConnected before calling this.
//
// Keyed by organizationId, so god-mode impersonation (which swaps the admin's
// own users/{uid}.organizationId) never serves one org's tax codes to another.
// Connecting or disconnecting QuickBooks mid-session goes stale until a page
// reload, which is acceptable for a display-only fallback.
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

const resultCache = new Map(); // organizationId -> { accounts, taxCodes }
const inFlight = new Map();    // organizationId -> Promise

export const getQuickBooksMappingOptionsCached = (organizationId) => {
    if (!organizationId) return Promise.resolve(null);

    if (resultCache.has(organizationId)) {
        return Promise.resolve(resultCache.get(organizationId));
    }

    if (inFlight.has(organizationId)) {
        return inFlight.get(organizationId);
    }

    const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
    const promise = getOptions()
        .then((result) => {
            const data = result?.data || null;
            // Cache only a successful, non-empty payload.
            if (data) resultCache.set(organizationId, data);
            inFlight.delete(organizationId);
            return data;
        })
        .catch((error) => {
            // Never cache a failure, and clear the in-flight entry so a later
            // call can retry. One transient blip must not kill tax codes for
            // the whole session.
            inFlight.delete(organizationId);
            throw error;
        });

    inFlight.set(organizationId, promise);
    return promise;
};
